import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  useGetUsersSyncQuery,
  useLinkIdentityBackendUserMutation,
  useProvisionBackendUserForIdentityMutation,
  useSearchBackendUsersQuery,
} from "../../services/rtkApi/adminApi";

const Panel = styled.section`
  width: min(1100px, 100%);
  margin: 20px 0;
  padding: 20px;
  border: 2px solid #111;
  background: rgba(255, 255, 255, 0.75);
  box-sizing: border-box;

  @media (max-width: 700px) {
    margin: 12px 0;
    padding: 16px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin: 16px 0 20px;
`;

const SummaryCard = styled.div`
  padding: 12px;
  border: 1px solid #111;
  background: #fff;
`;

const Row = styled.div<{ $selected?: boolean }>`
  border: 2px solid ${(p) => (p.$selected ? "#0a7" : "#111")};
  background: ${(p) => (p.$selected ? "rgba(204, 255, 238, 0.55)" : "#fff")};
  padding: 14px;
  margin-bottom: 12px;
  scroll-margin-top: 12px;
  overflow-wrap: anywhere;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const SearchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 100%;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #111;
  box-sizing: border-box;
`;

const InlineButton = styled.button`
  border: 1px solid #111;
  background: #111;
  color: #fff;
  padding: 8px 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(InlineButton)`
  background: #fff;
  color: #111;
`;

const Muted = styled.p`
  margin: 6px 0 0;
  font-size: 0.9rem;
  opacity: 0.8;
`;

type SyncRow = {
  identity: {
    _id: string;
    userId: string;
    username: string;
    email: string;
    role: string;
  } | null;
  backendUser: {
    _id: string;
    username: string;
    email: string;
    role: string;
  } | null;
  differences: string[];
  reason?: string;
};

type SearchResult = {
  userId: string;
  username: string;
  email: string;
  role: string;
  verifiedEmail: boolean;
};

const pickInitialSearchQuery = (row: SyncRow) => {
  const uid = row.identity?.userId?.trim() || "";
  const email = row.identity?.email?.trim() || "";
  const user = row.identity?.username?.trim() || "";
  if (/^[a-f0-9]{24}$/i.test(uid)) {
    return uid;
  }
  if (email.length > 0) {
    return email;
  }
  if (user.length > 0) {
    return user;
  }
  return uid;
};

const renderRowTitle = (row: SyncRow) => {
  if (row.identity && row.backendUser) {
    return `${row.identity.username || row.identity.email} -> ${row.backendUser.username}`;
  }

  if (row.identity) {
    return row.identity.username || row.identity.email;
  }

  if (row.backendUser) {
    return row.backendUser.username || row.backendUser.email;
  }

  return "unknown row";
};

export default function ManualIdentityLinkPanel() {
  const { data, isLoading, isFetching, error } = useGetUsersSyncQuery();
  const [selectedIdentityId, setSelectedIdentityId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [forceRelink, setForceRelink] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [linkIdentityBackendUser, { isLoading: isLinking }] =
    useLinkIdentityBackendUserMutation();
  const [provisionBackendUser, { isLoading: isProvisioning }] =
    useProvisionBackendUserForIdentityMutation();

  const backendSearchEnabled =
    selectedIdentityId !== null && searchQuery.trim().length >= 1;
  const { data: backendCandidates = [], isFetching: isSearching } =
    useSearchBackendUsersQuery(
      { query: searchQuery.trim(), limit: 8 },
      { skip: !backendSearchEnabled },
    );

  const problemRows = useMemo(
    () => ([...(data?.authOnly || []), ...(data?.mismatches || [])] as SyncRow[]),
    [data],
  );

  const handleLink = async (candidate: SearchResult) => {
    if (!selectedIdentityId) {
      return;
    }

    try {
      const result = await linkIdentityBackendUser({
        identityId: selectedIdentityId,
        backendUserId: candidate.userId,
        force: forceRelink,
      }).unwrap();

      if (
        result &&
        typeof result === "object" &&
        "success" in result &&
        (result as { success: boolean }).success === false
      ) {
        const errMsg =
          (result as { error?: string }).error || "link request rejected";
        setFeedback(errMsg);
        return;
      }

      const r = result as {
        identityId: string;
        backendUserId: string;
        previousBackendUserId: string;
      };
      setFeedback(
        `linked identity ${r.identityId} to backend user ${r.backendUserId}; old backend user was ${r.previousBackendUserId}`,
      );
      setSelectedIdentityId(null);
      setSearchQuery("");
      setForceRelink(false);
    } catch (linkError: any) {
      const data = linkError?.data;
      const message =
        (typeof data === "string" ? data : null) ||
        data?.message ||
        (Array.isArray(data?.message) ? data.message.join(", ") : null) ||
        data?.error ||
        linkError?.error ||
        "failed to link identity";
      setFeedback(String(message));
    }
  };

  const handleProvision = async (identityIdStr: string) => {
    try {
      const result = await provisionBackendUser({
        identityId: identityIdStr,
      }).unwrap();
      if (result?.created === false) {
        setFeedback(
          `backend user ${result.userId} already exists in auth's backend db — refresh or check BACKEND_MONGODB_URI.`,
        );
      } else {
        setFeedback(
          `created backend user row _id=${result.userId} (matches stored userId). tokens keep the same backendUserId.`,
        );
      }
    } catch (provisionError: any) {
      const errData = provisionError?.data;
      const message =
        (typeof errData === "string" ? errData : null) ||
        errData?.message ||
        (Array.isArray(errData?.message) ? errData.message.join(", ") : null) ||
        errData?.error ||
        provisionError?.error ||
        "provision failed";
      setFeedback(String(message));
    }
  };

  return (
    <Panel>
      <h2>manual identity linking</h2>
      <Muted>
        two repairs: (1) <strong>create missing backend profile</strong> — inserts a{" "}
        <code>users</code> document with the <em>same</em> <code>_id</code> as auth&apos;s{" "}
        <code>userId</code> when the row never existed in the db auth uses (no session revoke).
        (2) <strong>repair link</strong> — point auth at a different existing backend user (revokes
        sessions). fix <code>BACKEND_MONGODB_URI</code> first so both see the real app database.
      </Muted>

      {isLoading ? <p>loading identity sync...</p> : null}
      {error ? <p>failed to load identity sync.</p> : null}

      {data?.summary ? (
        <>
          <Muted>
            totals: {data.summary.totalIdentities ?? "?"} auth identities,{" "}
            {data.summary.totalBackendUsers ?? "?"} backend users
          </Muted>
          <SummaryGrid>
            <SummaryCard>
              <strong>{data.summary.matched}</strong>
              <div>linked &amp; matching</div>
            </SummaryCard>
            <SummaryCard style={data.summary.authOnly > 0 ? { borderColor: "#c00" } : {}}>
              <strong>{data.summary.authOnly}</strong>
              <div>auth → no backend user</div>
            </SummaryCard>
            <SummaryCard style={data.summary.backendOnly > 0 ? { borderColor: "#c00" } : {}}>
              <strong>{data.summary.backendOnly}</strong>
              <div>backend → no auth identity</div>
            </SummaryCard>
            <SummaryCard style={data.summary.mismatches > 0 ? { borderColor: "#e90" } : {}}>
              <strong>{data.summary.mismatches}</strong>
              <div>linked but fields differ</div>
            </SummaryCard>
          </SummaryGrid>
        </>
      ) : null}

      {feedback ? <p>{feedback}</p> : null}
      {isFetching ? <Muted>refreshing sync data...</Muted> : null}

      <h3>needs repair</h3>
      {problemRows.length === 0 ? <p>no broken links found.</p> : null}

      {problemRows.map((row) => {
        const identityId = String(row.identity?._id ?? "");
        const isSelected = selectedIdentityId === identityId;
        const canProvisionMissing =
          row.differences.includes("missing_backend_user") &&
          Boolean(row.identity?.userId?.trim());

        return (
          <Row
            key={`${identityId}-${row.backendUser?._id || "missing"}`}
            id={identityId ? `identity-repair-${identityId}` : undefined}
            $selected={isSelected}
          >
            <strong>{renderRowTitle(row)}</strong>
            {row.reason ? (
              <Muted style={{ color: "#c00" }}>reason: {row.reason}</Muted>
            ) : (
              <Muted>differences: {row.differences.join(", ")}</Muted>
            )}
            {row.identity ? (
              <Muted>
                auth identity: {row.identity._id} | email: {row.identity.email} | stored
                userId: {row.identity.userId || "(none)"}
              </Muted>
            ) : null}
            {row.backendUser ? (
              <Muted>
                backend user: {row.backendUser._id} | email: {row.backendUser.email}
              </Muted>
            ) : null}

            {row.identity ? (
              <ActionRow>
                {canProvisionMissing ? (
                  <InlineButton
                    type="button"
                    disabled={isProvisioning || isLinking}
                    onClick={() => {
                      setFeedback(null);
                      void handleProvision(identityId);
                    }}
                  >
                    {isProvisioning ? "creating…" : "create missing backend profile"}
                  </InlineButton>
                ) : null}
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    if (isSelected) {
                      setSelectedIdentityId(null);
                      setFeedback(null);
                      return;
                    }
                    setSelectedIdentityId(identityId);
                    setSearchQuery(pickInitialSearchQuery(row));
                    setFeedback(null);
                    requestAnimationFrame(() => {
                      if (identityId) {
                        document
                          .getElementById(`identity-repair-${identityId}`)
                          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                      }
                    });
                  }}
                >
                  {isSelected ? "close relink" : "relink to another backend user"}
                </SecondaryButton>
              </ActionRow>
            ) : null}

            {isSelected && row.identity ? (
              <>
                <Muted style={{ marginTop: "12px", fontWeight: 600 }}>
                  relink mode: pick an <em>existing</em> backend user below (search by username,
                  email, or ObjectId). use &quot;create missing backend profile&quot; if the row for
                  stored userId should exist but doesn&apos;t.
                </Muted>
                <SearchInput
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="search backend users by username or email"
                />
                <label style={{ display: "block", marginTop: "10px" }}>
                  <input
                    type="checkbox"
                    checked={forceRelink}
                    onChange={(event) => setForceRelink(event.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  allow email mismatch override
                </label>
                {isSearching ? <Muted>searching backend users...</Muted> : null}
                {!backendSearchEnabled ? (
                  <Muted>type at least 1 character to search (stored userId is pre-filled when it looks like an ObjectId).</Muted>
                ) : null}
                <SearchList>
                  {backendSearchEnabled && !isSearching && backendCandidates.length === 0 ? (
                    <Muted>
                      no backend users matched. fix <code>BACKEND_MONGODB_URI</code> on auth to match
                      backend <code>MONGODB_URI</code>, or use &quot;create missing backend
                      profile&quot; if this identity&apos;s userId has no row yet.
                    </Muted>
                  ) : null}
                  {backendCandidates.map((candidate) => (
                    <Row key={candidate.userId}>
                      <strong>{candidate.username}</strong>
                      <Muted>
                        {candidate.email || "no email"} | role: {candidate.role} | id:{" "}
                        {candidate.userId}
                      </Muted>
                      <ActionRow>
                        <InlineButton
                          type="button"
                          disabled={isLinking}
                          onClick={() => handleLink(candidate)}
                        >
                          link to this backend user
                        </InlineButton>
                        <SecondaryButton
                          type="button"
                          onClick={() => setSearchQuery(candidate.email || candidate.username)}
                        >
                          use as search
                        </SecondaryButton>
                      </ActionRow>
                    </Row>
                  ))}
                </SearchList>
              </>
            ) : null}
          </Row>
        );
      })}

      <h3>backend-only users</h3>
      {(data?.backendOnly || []).length === 0 ? (
        <p>no backend-only users found.</p>
      ) : (
        (data?.backendOnly || []).map((row: SyncRow) => (
          <Row key={row.backendUser?._id || "backend-only"}>
            <strong>{row.backendUser?.username || row.backendUser?.email}</strong>
            <Muted>
              backend user: {row.backendUser?._id} | email: {row.backendUser?.email} |
              differences: {row.differences.join(", ")}
            </Muted>
          </Row>
        ))
      )}
    </Panel>
  );
}
